/**
 * UFT Page Interrupts: a registry of whatever currently occupies the visitor's attention
 * (a consent banner, a vendor overlay, a guided tour). Each claims a named hold while on
 * screen and releases it on the way out; anything that wants to interrupt checks
 * `isHeld()` first, so two widgets never pile up. Every change dispatches
 * `page:interrupts-changed` on the theme's event bus. Every claim carries a TTL, so a
 * claimant that never releases costs one quiet stretch rather than the session.
 *
 * Usage:
 *   window.UFT.PageInterrupts.claim('truevault-consent');
 *   window.UFT.PageInterrupts.release('truevault-consent');
 *   if (window.UFT.PageInterrupts?.isHeld()) { ... }
 */

window.UFT = window.UFT || {};
window.UFT.PageInterrupts =
	window.UFT.PageInterrupts ||
	(function () {
		// Long enough that a visitor reading a consent banner is never cut off,
		// short enough that a stuck claim cannot silence a whole session.
		const DEFAULT_MAX_HOLD_MS = 90 * 1000;

		class PageInterrupts {
			#holds = new Map();

			/**
			 * @param {string} name - Identifies the claimant. Re-claiming resets its TTL.
			 * @param {Object} [options]
			 * @param {number} [options.maxHoldMs] - Release the claim automatically after this long
			 */
			claim(name, { maxHoldMs = DEFAULT_MAX_HOLD_MS } = {}) {
				if (!this._isValidName(name)) {
					return;
				}

				// Without the bus a release could never reach the consumers that paused, so a refused
				// claim costs one extra popup instead of a silenced visit.
				if (!window.UFT.EventManager) {
					return;
				}

				const alreadyHeld = this.#holds.has(name);
				this._restartExpiry(name, maxHoldMs);

				if (!alreadyHeld) {
					this._notifyChanged();
				}
			}

			/**
			 * @param {string} name - The claim to drop. Releasing an unheld name does nothing.
			 */
			release(name) {
				if (!this.#holds.has(name)) {
					return;
				}

				clearTimeout(this.#holds.get(name));
				this.#holds.delete(name);
				this._notifyChanged();
			}

			/**
			 * @returns {boolean} Whether anything currently has the visitor's attention
			 */
			isHeld() {
				return this.#holds.size > 0;
			}

			// Reported, not swallowed: a nameless claim is a caller bug and an unreleasable hold is
			// worse than none.
			_isValidName(name) {
				if (typeof name === 'string' && name.length > 0) {
					return true;
				}

				console.error('[PageInterrupts] A claim needs a non-empty name.', name);
				return false;
			}

			_restartExpiry(name, maxHoldMs) {
				clearTimeout(this.#holds.get(name));
				this.#holds.set(
					name,
					setTimeout(() => this.release(name), maxHoldMs)
				);
			}

			// The bus is checked at claim time, and a hold cannot exist without one.
			_notifyChanged() {
				const events = window.UFT.EventManager;
				events.dispatch(events.eventStrings.PAGE_INTERRUPTS_CHANGED);
			}
		}

		return new PageInterrupts();
	})();
